import './Dropdown.scss'
import { Typography } from '../Typography'
import React from 'react'

type DropdownOption = {
  content: string | React.ReactElement
  onSelect?: () => void
  key: string | number
  Icon?: React.ReactElement
}

export interface DropdownProps extends React.ComponentPropsWithoutRef<'link'> {
  options?: DropdownOption[]
  className?: string
  borderless?: boolean
  listClassName?: string
}

/** A Dropdown list that can be altered to be a list with or without border and or icon in each list item.
 * @param borderless - boolean to show/hide border around each list item
 * @param className - the classname you want applied to the container div of this element.
 * @param listClassName - the classname you want applied to the li element.
 * @param options - an array of options config objects that form your dropdown items.
 * @param content - inside options array - your text to be displayed for each item in the list.
 * Can be string, in which case it will be rendered inside Typography component or it can be a component of your choice
 * @param Icon - variable to render the icon component
 * @author Arwa Kuterwadliwala
 */

export const Dropdown: React.FC<DropdownProps> = ({
  options,
  className = '',
  borderless = false,
  listClassName = '',
}) => {
  const handleOptionSelected = (callBack: () => void) => {
    callBack()
  }

  const addBorder = borderless ? '' : 'CLDropdown__option--border'
  const addPadding = borderless ? 'CLDropdown--padded' : ''

  return (
    <div className={`CLDropdown ${className} ${addPadding}`}>
      {options.map(({ content, onSelect, key, Icon }) => (
        <li
          key={key}
          className={`CLDropdown__option ${addBorder} ${listClassName}`}
        >
          <button
            role="link"
            aria-label="button like dropdown link"
            className="CLDropdown__option__button"
            onClick={
              handleOptionSelected ? () => handleOptionSelected(onSelect) : null
            }
          >
            {typeof content === 'string' ? (
              <Typography className="CLDropdown__option__typography">
                {content}
              </Typography>
            ) : (
              React.cloneElement(content, {
                className:
                  content.props.className + ' CLDropdown__option__typography',
              })
            )}
            {React.isValidElement(Icon)
              ? React.cloneElement(Icon as React.ReactElement, {
                  className: Icon.props + ' CLDropdown__option__icon',
                })
              : ''}
          </button>
        </li>
      ))}
    </div>
  )
}
